import { useEffect } from "react";
import { useLocation } from "wouter";
import { ProjectTree } from "@/components/project-tree";
import { PulseWorkbenchProvider } from "@/contexts/pulse-workbench-context";
import { WorkbenchShell } from "@/components/pulse-workbench";
import { useExpansion } from "@/contexts/expansion-context";
import { consumeFromSearchIntent } from "@/lib/quick-search/focus-intent";

/**
 * Reads `?projectId=…` from the URL on mount (and whenever it changes)
 * and drives the same focus pipeline that the quick-search palette and
 * the pulse-notification dropdown use:
 *
 *   1. Always: dispatch `navigate-to-project` so the tree expands
 *      year + project and triggers its smooth scroll-into-view.
 *   2. Only when the navigation originated from the quick-search
 *      palette (consumed via `consumeFromSearchIntent`):
 *      additionally call `collapseAllExceptYears()`.
 *
 * The split (task #250) ensures that:
 *   - Share-link / bookmark / page refresh / browser back-forward of
 *     `/?projectId=X` only focuses the project — the user's other
 *     expanded projects stay open.
 *   - Clicking a quick-search hit still collapses everything except
 *     years before focusing.
 *
 * This is also the cross-route safety net for task #249: when a user
 * clicks a search hit from an admin/settings route the
 * QuickSearchDialog's own event dispatch is lost because the tree
 * isn't mounted yet — but by the time this effect fires the tree's
 * `navigate-to-project` listener is attached, so the event lands
 * cleanly. The quick-search dialog sets the from-search intent BEFORE
 * navigating, so it survives the cross-route mount.
 */
function FocusFromUrlSearchParam() {
  const [location] = useLocation();
  const { collapseAllExceptYears } = useExpansion();

  useEffect(() => {
    // wouter's `location` is the pathname only — query string lives on
    // `window.location.search`. Re-read it whenever the path changes so
    // back/forward navigation between two `?projectId=…` URLs still
    // refocuses.
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get("projectId");
    if (!projectId) return;

    // Read-and-clear the transient flag. True only when this mount was
    // triggered by a quick-search click — refreshes, share-links, and
    // browser back-forward navigations never carry it.
    if (consumeFromSearchIntent()) {
      collapseAllExceptYears();
    }
    // Defer one frame so the ProjectTree's `navigate-to-project`
    // listener is definitely attached before we dispatch — covers the
    // first paint after a cross-route navigation.
    const raf = requestAnimationFrame(() => {
      window.dispatchEvent(
        new CustomEvent("navigate-to-project", {
          detail: { projectId },
        }),
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [location, collapseAllExceptYears]);

  return null;
}

export default function ProjectDashboard() {
  return (
    <PulseWorkbenchProvider>
      <FocusFromUrlSearchParam />
      <ProjectTree />
      <WorkbenchShell />
    </PulseWorkbenchProvider>
  );
}
